import { useRef, useState } from 'react';
import { compressImage, type CompressedPhoto } from '../lib/image';

interface Props {
  max: number;
  required?: boolean;
  value: CompressedPhoto[];
  onChange: (photos: CompressedPhoto[]) => void;
  hint?: string;
}

export function PhotoPicker({ max, required, value, onChange, hint }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // reset เพื่อให้เลือกไฟล์เดิมซ้ำได้
    if (files.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      const slots = Math.max(0, max - value.length);
      const toProcess = files.slice(0, slots);
      const compressed = await Promise.all(toProcess.map(compressImage));
      onChange([...value, ...compressed]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาด');
    } finally {
      setBusy(false);
    }
  }

  function removeAt(i: number) {
    const next = value.slice();
    const removed = next.splice(i, 1)[0];
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    onChange(next);
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        {value.map((p, i) => (
          <div
            key={p.previewUrl}
            className="relative aspect-square overflow-hidden rounded-2xl border border-gray-200 bg-gray-50"
          >
            <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white"
              aria-label="ลบรูป"
            >
              ✕
            </button>
          </div>
        ))}
        {Array.from({ length: Math.max(0, max - value.length) }).map((_, i) => (
          <button
            key={`slot-${i}`}
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 active:bg-gray-100 disabled:opacity-50"
          >
            <span className="text-3xl">📷</span>
            <span className="text-xs">{busy ? 'กำลังย่อ...' : 'เพิ่มรูป'}</span>
          </button>
        ))}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={pick}
      />

      <p className="mt-2 text-xs text-gray-500">
        {hint ?? `อย่างน้อย ${required ? 1 : 0} รูป สูงสุด ${max} รูป (ระบบจะย่อขนาดให้อัตโนมัติ)`}
      </p>

      {error && (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
